import React, { useRef, useState } from "react";
import styled from "styled-components";
import DataTable from "../components/DataTable";
import apiClient from "../utils/apiClient";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Chip, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import LinearProgressWithLabel from "../components/LinearProgressWithLabel";
import FileSaver from "file-saver";
import { useTranslation } from "react-i18next";

const Title = styled.h1`
  font-weight: 500;
  font-size: 24px;
  line-height: 110%;
  margin-top: 0;
`;

const HeadTabMenu = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 20px;
  gap: 10px;

  & > div:not(:last-child) {
    margin-inline-end: 10px;
  }

  & > div:not(:first-child) {
    margin-inline-start: 10px;
  }
`;

const ButtonsBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 0;
  position: relative;
  overflow: visible;
  gap: 5px;
`;

const Documents = () => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user.user);

  const [refreshCount, setRefreshCount] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fileInputRef = useRef(null);

  const handleChooseFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    console.log("Selected file:", file);
    if (!file) return;

    setUploadProgress(0);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("requiresSignature", false);

    for (let pair of formData.entries()) {
      console.log(`${pair[0]}:`, pair[1]);
    }

    try {
      await apiClient.post("/documents", formData, {
        onUploadProgress: (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setUploadProgress(percent);
        },
      });
      alert(t("documents.fileUploaded"));
      setRefreshCount((prev) => prev + 1);
    } catch (err) {
      console.error(t("documents.uploadErrorLog"), err);
      alert(t("documents.uploadError"));
    } finally {
      e.target.value = null;
      setUploadProgress(0);
    }
  };

  const downloadDocument = async (document) => {
    try {
      const response = await apiClient.get(
        `/documents/download/${document.id}`,
        {
          responseType: "blob",
        },
      );
      const blob = new Blob([response.data]);
      FileSaver.saveAs(blob, document.original_name);
    } catch (error) {
      console.error(t("documents.downloadErrorLog"), error);
    }
  };

  const columns = [
    {
      field: "user_id",
      headerName: t("documents.columns.uploadedBy"),
      flex: 1,
      renderCell: (params) => {
        if (params.row.user_id === user.id) {
          return <span>{t("documents.uploadedByYou")}</span>;
        }
        return <span>{t("documents.uploadedByAdmin")}</span>;
      },
    },
    {
      field: "uploaded_at",
      headerName: t("documents.columns.uploadedAt"),
      flex: 1,
      valueFormatter: (value) => {
        return dayjs(value).format("DD.MM.YYYY HH:mm:ss");
      },
    },
    {
      field: "original_name",
      headerName: t("documents.columns.name"),
      flex: 2,
    },
    {
      field: "signStatus",
      headerName: t("documents.columns.status"),
      flex: 1,
      renderCell: (params) => {
        if (params.row.is_signed) {
          return (
            <Chip
              size="small"
              variant="outlined"
              color="success"
              label={t("documents.status.signed")}
            />
          );
        } else if (params.row.requires_signature) {
          return (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label={t("documents.status.waitingSignature")}
            />
          );
        }
        return <span>-</span>;
      },
    },
    {
      field: "action",
      headerName: "",
      width: 180,
      type: "actions",
      renderCell: (params) => {
        return (
          <ButtonsBlock>
            <IconButton
              aria-label="download document"
              onClick={() => {
                downloadDocument(params.row);
              }}
              title={t("documents.downloadDocument")}
            >
              <CloudDownloadIcon />
            </IconButton>

            {params.row.user_id === user.id && (
              <IconButton
                aria-label="delete document"
                onClick={async () => {
                  if (window.confirm(t("documents.confirmDelete"))) {
                    await apiClient.delete(`/documents/${params.row.id}`);
                    setRefreshCount((prev) => prev + 1);
                  }
                }}
                title={t("documents.deleteDocument")}
              >
                <DeleteIcon />
              </IconButton>
            )}

            {params.row.requires_signature && !params.row.is_signed ? (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={async () => {
                  try {
                    await apiClient.post(`/documents/${params.row.id}/sign`);
                    const code = prompt(t("documents.enterSmsCode"));
                    if (code) {
                      const resp = await apiClient.post(
                        `/documents/${params.row.id}/sign/verify`,
                        { code },
                      );
                      if (resp.data.success) {
                        alert(t("documents.documentSigned"));
                        setRefreshCount((prev) => prev + 1);
                      }
                    }
                  } catch (err) {
                    alert(
                      err.response?.data?.error || t("documents.signError"),
                    );
                  }
                }}
              >
                {t("documents.sign")}
              </Button>
            ) : (
              ""
            )}
          </ButtonsBlock>
        );
      },
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t("documents.metaTitle")}</title>
        <meta name="description" content={t("documents.metaDescription")} />
      </Helmet>

      <Title>{t("documents.title")}</Title>

      <HeadTabMenu>
        <Button
          variant="contained"
          color="primary"
          onClick={handleChooseFile}
          fullWidth
          startIcon={<AddIcon />}
        >
          {t("documents.addFile")}
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setRefreshCount((prev) => prev + 1)}
          fullWidth
        >
          {t("common.refresh")}
        </Button>

        <input
          style={{ display: "none" }}
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </HeadTabMenu>

      {uploadProgress > 0 && (
        <Box sx={{ width: "50%", mb: 2 }}>
          <LinearProgressWithLabel value={uploadProgress} color="primary" />
        </Box>
      )}

      <DataTable columns={columns} path={"/documents"} refresh={refreshCount} />
    </>
  );
};

export default Documents;
